import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUserCompanies } from '@/data/api/companies';
import { useTranslation } from 'react-i18next';
import { Building02, CheckCircle } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { switchCompany } from '@/hooks/api/use-auth';
import { setAuthToken } from '@/lib/api-client';
import { storage } from '@/lib/local-storage';
import { toast } from '@/lib/toast-service';
import { useMe } from '@/hooks/api/use-auth';

export const ProfileCompaniesList = () => {
    const { t } = useTranslation(['pages:profile', 'common']);
    const queryClient = useQueryClient();
    const { data: currentUser } = useMe();
    const [switchingCompanyId, setSwitchingCompanyId] = useState<string | null>(null);
    
    const { data: companies = [], isLoading } = useQuery({
        queryKey: ['user-companies'],
        queryFn: getUserCompanies,
        staleTime: 5 * 60 * 1000,
    });

    const handleSetPrimaryCompany = async (companyId: string) => {
        // Don't switch if already primary
        if (currentUser?.company_id === companyId) {
            return;
        }

        setSwitchingCompanyId(companyId);

        try {
            // Call API to switch company and get new tokens
            const tokenResponse = await switchCompany(companyId);

            // Update tokens in localStorage
            setAuthToken(tokenResponse.access_token);
            storage.setItem("refresh_token", tokenResponse.refresh_token);

            // Update user info in query cache
            queryClient.setQueryData(["auth", "me"], tokenResponse.user);

            // Invalidate user companies query to refresh the list
            queryClient.invalidateQueries({ queryKey: ["user-companies"] });

            toast.success(t('companies.primaryChanged', { defaultValue: 'Company principale cambiata con successo' }));
        } catch (error: any) {
            console.error('Error switching company:', error);
            toast.error(error?.message || t('companies.errorChangingPrimary', { defaultValue: 'Errore durante il cambio della company principale' }));
        } finally {
            setSwitchingCompanyId(null);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="text-tertiary">{t('common:loading')}</div>
            </div>
        );
    }

    if (companies.length === 0) {
        return (
            <div className="text-center py-8 text-tertiary">
                {t('companies.noCompanies')}
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {companies.map((company) => {
                const isPrimary = company.is_primary_company || currentUser?.company_id === company.company_id;
                const isSwitching = switchingCompanyId === company.company_id;

                return (
                    <div
                        key={company.company_id}
                        className={`flex items-center justify-between py-3 px-4 rounded-lg ${
                            isPrimary ? 'bg-brand-secondary border border-brand-primary' : 'bg-secondary'
                        }`}
                    >
                        <div className="flex items-center gap-3">
                            <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${
                                isPrimary ? 'bg-brand-primary' : 'bg-tertiary'
                            }`}>
                                <Building02 className={`size-5 ${isPrimary ? 'text-white' : 'text-quaternary'}`} />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-primary">
                                        {company.company_name}
                                    </p>
                                    {isPrimary && (
                                        <CheckCircle className="size-4 text-brand-primary" />
                                    )}
                                </div>
                                <p className="text-xs text-tertiary">
                                    {company.company_type ? 
                                        t(`companies.types.${company.company_type}`) : 
                                        t('companies.noType')
                                    } • {t(`companies.role.${company.role}`)}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {isPrimary ? (
                                <span className="text-xs px-2 py-1 bg-brand-primary text-white rounded font-medium">
                                    {t('companies.primary')}
                                </span>
                            ) : (
                                <Button
                                    color="secondary"
                                    size="sm"
                                    onClick={() => handleSetPrimaryCompany(company.company_id)}
                                    isDisabled={isSwitching}
                                >
                                    {isSwitching 
                                        ? t('common:loading', { defaultValue: 'Caricamento...' })
                                        : t('companies.setAsPrimary', { defaultValue: 'Imposta come principale' })
                                    }
                                </Button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

